import { forwardRef, useEffect, useRef } from 'react';
import Cropper from 'cropperjs';
import { useDeepCompareEffect } from '../hooks/use-compare-effect.js';
import { useCombinedRefs } from '../utils/react-helpers.js';

export interface CropperElement extends HTMLImageElement {
  cropper: Cropper;
}

export type CropperRef =
  | ((instance: HTMLImageElement | CropperElement | null) => void)
  | React.MutableRefObject<HTMLImageElement | CropperElement | null>
  | null;

export interface CropperDefaultOptions {
  scaleX?: number;
  scaleY?: number;
  enable?: boolean;
  zoomTo?: number;
  rotateTo?: number;
}

export interface CropperProps
  extends CropperDefaultOptions,
    Cropper.Options<HTMLImageElement>,
    Omit<React.HTMLProps<HTMLImageElement>, 'data' | 'ref' | 'crossOrigin'> {
  crossOrigin?: '' | 'anonymous' | 'use-credentials' | undefined;
  on?: (
    eventName: string,
    callback: () => void | Promise<void>
  ) => void | Promise<void>;
  onInitialized?: (instance: Cropper) => void | Promise<void>;
}

const applyDefaultOptions = (
  cropper: Cropper,
  options: CropperDefaultOptions = {}
): void => {
  const {
    enable = true,
    scaleX = 1,
    scaleY = 1,
    zoomTo = 0,
    rotateTo,
  } = options;
  if (enable) {
    cropper.enable();
  } else {
    cropper.disable();
  }
  cropper.scaleX(scaleX);
  cropper.scaleY(scaleY);
  if (rotateTo !== undefined) {
    cropper.rotateTo(rotateTo);
  }
  if (zoomTo > 0) {
    cropper.zoomTo(zoomTo);
  }
};

export const CropperJs = forwardRef<
  CropperElement | HTMLImageElement,
  CropperProps
>((props, ref) => {
  const {
    dragMode = 'crop',
    src,
    style,
    className,
    crossOrigin,
    scaleX,
    scaleY,
    enable,
    zoomTo,
    rotateTo,
    data,
    alt = 'picture',
    // https://github.com/fengyuanchen/cropperjs/blob/main/README.md#viewmode
    viewMode = 2,
    aspectRatio = 1 / 1,
    ready,
    onInitialized,
    ...rest
  } = props;

  const defaultOptions: CropperDefaultOptions = {
    scaleY,
    scaleX,
    enable,
    zoomTo,
    rotateTo,
  };
  const innerRef = useRef<HTMLImageElement>(null);
  const combinedRef = useCombinedRefs<CropperElement>(ref, innerRef);

  /**
   * Invoke zoomTo method when cropper is set and zoomTo prop changes
   */
  useEffect(() => {
    if (combinedRef.current?.cropper && typeof zoomTo === 'number') {
      combinedRef.current.cropper.zoomTo(zoomTo);
    }
  }, [combinedRef, zoomTo]);

  /**
   * re-render when src changes
   */
  useEffect(() => {
    if (combinedRef.current?.cropper && typeof src !== 'undefined') {
      combinedRef.current.cropper.reset().clear().replace(src);
    }
  }, [combinedRef, src]);

  useDeepCompareEffect(() => {
    const cropperElm = combinedRef.current;
    if (cropperElm !== null) {
      const cropper = new Cropper(cropperElm, {
        dragMode,
        viewMode,
        aspectRatio,
        data,
        ...rest,
        ready: (e) => {
          if (e.currentTarget !== null) {
            applyDefaultOptions(e.currentTarget.cropper, defaultOptions);
          }
          if (ready) {
            ready(e);
          }
        },
      });
      if (onInitialized) {
        void onInitialized(cropper);
      }
    }
    /**
     * destroy cropper on un-mount
     */
    return () => {
      cropperElm?.cropper?.destroy();
    };
  }, [
    combinedRef,
    ready,
    onInitialized,
    defaultOptions,
    rest,
    { aspectRatio, viewMode, dragMode, data },
  ]);

  return (
    <div style={style} className={className}>
      <img
        crossOrigin={crossOrigin}
        src={src}
        alt={alt}
        style={{ opacity: 0, maxWidth: '100%', display: 'none' }}
        ref={combinedRef}
      />
    </div>
  );
});
